import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Box,
    Typography
} from '@mui/material';
import { styled } from '../../../shared/Theme/theme';
import { AuditLogGroup } from '../../../../models/entities/audit/auditLog';
import { formatDistanceToNow } from 'date-fns';

const StyledPaper = styled(Paper)`
    box-shadow: none !important;
    border: 1px solid ${props => props.theme.colors.grayDefaultBorder};
`;

const StyledTableContainer = styled(TableContainer)`
    max-height: calc(100vh - 200px);
    overflow: auto;
`;

const StyledTableCell = styled(TableCell)`
    font-weight: 600 !important;
    background-color: #fff !important;
    border-bottom: 3px solid ${props => props.theme.colors.primaryBlue} !important;
    color: ${props => props.theme.colors.secondary} !important;
    font-size: ${props => props.theme.fontSizes.gridCell} !important;
    height: 76px !important;
    position: sticky;
    top: 0;
    z-index: 10;
`;

const StyledBodyCell = styled(TableCell)`
    border-bottom: solid 1px ${props => props.theme.colors.grayDefaultBorder} !important;
    color: ${props => props.theme.colors.gridCell} !important;
    font-size: ${props => props.theme.fontSizes.gridCell} !important;
    padding: 6px !important;
`;

const ActionChip = styled(Chip)<{ actiontype: string }>`
    font-weight: 600 !important;
    min-width: 80px;
    background-color: ${props =>
        props.actiontype === 'CREATE' ? '#4caf50' :
        props.actiontype === 'UPDATE' ? props.theme.colors.primaryBlue :
        props.actiontype === 'DELETE' ? '#f44336' : props.theme.colors.grey
    } !important;
    color: white !important;
`;

const UserCell = styled.div`
    display: flex;
    flex-direction: column;
`;

const UserName = styled.span`
    font-weight: 600;
    color: ${props => props.theme.colors.gridCell};
`;

const UserRole = styled.span`
    font-size: 0.75rem;
    color: ${props => props.theme.colors.darkBlue};
`;

const EntityCell = styled.div`
    display: flex;
    flex-direction: column;
`;

const EntityName = styled.span`
    font-weight: 600;
    color: ${props => props.theme.colors.gridCell};
`;

const EntityType = styled.span`
    font-size: 0.75rem;
    color: ${props => props.theme.colors.darkBlue};
    text-transform: capitalize;
`;

const TimestampCell = styled.div`
    display: flex;
    flex-direction: column;
`;

const TimestampMain = styled.span`
    font-weight: 500;
    color: ${props => props.theme.colors.gridCell};
`;

const TimestampRelative = styled.span`
    font-size: 0.75rem;
    color: ${props => props.theme.colors.darkBlue};
`;

const ChangesCell = styled(Box)`
    max-height: 150px;
    overflow-y: auto;
    padding: 4px 0;

    &::-webkit-scrollbar {
        width: 6px;
    }
    &::-webkit-scrollbar-thumb {
        background-color: ${props => props.theme.colors.grey};
        border-radius: 3px;
    }
    &::-webkit-scrollbar-track {
        background-color: ${props => props.theme.colors.grayDefaultBorder};
    }
`;

const ChangeItem = styled(Box)`
    margin-bottom: 6px;
    &:last-child {
        margin-bottom: 0;
    }
`;

const FieldName = styled.span`
    font-weight: 600;
    color: ${props => props.theme.colors.primaryBlue};
    font-size: 0.8rem;
`;

const OldValue = styled.span`
    text-decoration: line-through;
    color: #d32f2f;
    font-size: 0.8rem;
`;

const NewValue = styled.span`
    color: #388e3c;
    font-size: 0.8rem;
`;

const ReasonText = styled(Typography)`
    margin-top: 8px !important;
    padding: 6px 8px;
    background-color: #e3f2fd;
    border-left: 3px solid ${props => props.theme.colors.primaryBlue};
    border-radius: 4px;
    font-size: 0.75rem !important;
    font-style: italic;
`;

interface GroupedAuditTableProps {
    groups: AuditLogGroup[];
    loading?: boolean;
}

const GroupedAuditTable: React.FC<GroupedAuditTableProps> = ({
    groups,
    loading = false
}) => {
    const getActionLabel = (action: string): string => {
        switch (action) {
            case 'CREATE':
                return 'Created';
            case 'UPDATE':
                return 'Updated';
            case 'DELETE':
                return 'Deleted';
            default:
                return action;
        }
    };

    const formatTimestamp = (timestamp: string): { formatted: string; relative: string } => {
        try {
            const date = new Date(timestamp);
            const relativeTime = formatDistanceToNow(date, { addSuffix: true });
            const formattedDate = date.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
            return { formatted: formattedDate, relative: relativeTime };
        } catch (error) {
            return { formatted: timestamp, relative: '' };
        }
    };

    const formatValue = (value: any): string => {
        if (value === null || value === undefined || value === '') {
            return '(empty)';
        }
        if (typeof value === 'object') {
            return JSON.stringify(value);
        }
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }
        const stringValue = String(value);
        // Truncate very long values
        return stringValue.length > 100 ? stringValue.substring(0, 100) + '...' : stringValue;
    };

    if (groups.length === 0 && !loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" padding={4}>
                <Typography variant="body1" color="textSecondary">
                    No grouped audit logs found
                </Typography>
            </Box>
        );
    }

    return (
        <StyledPaper>
            <StyledTableContainer>
                <Table stickyHeader aria-label="grouped audit logs table">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Timestamp</StyledTableCell>
                            <StyledTableCell>User</StyledTableCell>
                            <StyledTableCell>Entity</StyledTableCell>
                            <StyledTableCell>Action</StyledTableCell>
                            <StyledTableCell>Changes</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {groups.map((group) => {
                            const timestamp = formatTimestamp(group.timestamp);
                            const maxChangesToShow = 5;

                            return (
                                <TableRow key={group.sessionId}>
                                    <StyledBodyCell>
                                        <TimestampCell>
                                            <TimestampMain>{timestamp.formatted}</TimestampMain>
                                            <TimestampRelative>{timestamp.relative}</TimestampRelative>
                                        </TimestampCell>
                                    </StyledBodyCell>
                                    <StyledBodyCell>
                                        <UserCell>
                                            <UserName>{group.user.name}</UserName>
                                            {group.user.role && <UserRole>{group.user.role}</UserRole>}
                                        </UserCell>
                                    </StyledBodyCell>
                                    <StyledBodyCell>
                                        <EntityCell>
                                            <EntityName>
                                                {group.entity.name || `#${group.entity.id}`}
                                            </EntityName>
                                            <EntityType>{group.entity.type}</EntityType>
                                        </EntityCell>
                                    </StyledBodyCell>
                                    <StyledBodyCell>
                                        <ActionChip
                                            label={getActionLabel(group.action)}
                                            actiontype={group.action}
                                            size="small"
                                        />
                                    </StyledBodyCell>
                                    <StyledBodyCell>
                                        <ChangesCell>
                                            {group.changes.slice(0, maxChangesToShow).map((change, idx) => (
                                                <ChangeItem key={change.id || idx}>
                                                    <FieldName>{change.fieldName || 'Unknown'}:</FieldName>
                                                    {' '}
                                                    <OldValue>{formatValue(change.oldValue)}</OldValue>
                                                    {' → '}
                                                    <NewValue>{formatValue(change.newValue)}</NewValue>
                                                </ChangeItem>
                                            ))}
                                            {group.changeCount > maxChangesToShow && (
                                                <Typography variant="caption" color="textSecondary" fontStyle="italic">
                                                    ... and {group.changeCount - maxChangesToShow} more changes
                                                </Typography>
                                            )}
                                            {group.reason && (
                                                <ReasonText variant="caption">
                                                    <strong>Reason:</strong> {group.reason}
                                                </ReasonText>
                                            )}
                                        </ChangesCell>
                                    </StyledBodyCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </StyledTableContainer>
        </StyledPaper>
    );
};

export default GroupedAuditTable;
